/**
 * @file PlatformTaxCard.tsx
 * @brief Standalone editor for the platform-wide tax rate.
 *
 * @author DES
 * @date 06-07-2026
 * @updated 29-07-2026 — GridBlock flush layout
 */

import { useEffect, useMemo, useState } from "react";
import { LoaderCircle, Percent, Save } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { FormFieldGroup } from "@/components/ui/FormDialog";
import {
  GridBand,
  GridCell,
} from "@/features/analytics/components/GridBlock";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { useUpdateSettings } from "../api";
import type { SystemSettingsResponse } from "../schemas";

export interface PlatformTaxCardProps {
  data: SystemSettingsResponse | undefined;
}

export function PlatformTaxCard({ data }: PlatformTaxCardProps) {
  const settings = data?.settings ?? {};

  const [taxPercent, setTaxPercent] = useState<string>("");

  useEffect(() => {
    const tax = Number(settings.TAX_RATE?.value ?? "0");
    setTaxPercent(Number.isFinite(tax) ? (tax * 100).toFixed(0) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const update = useUpdateSettings();

  const dirty = useMemo<boolean>(() => {
    const newTax = Number(taxPercent || "0") / 100;
    const storedTax = Number(settings.TAX_RATE?.value ?? 0);
    return Number.isFinite(newTax) && newTax !== storedTax;
  }, [taxPercent, settings]);

  const handleSave = async () => {
    if (!dirty) return;
    const newTax = Number(taxPercent || "0") / 100;
    try {
      await update.mutateAsync({ settings: { TAX_RATE: newTax.toString() } });
      toast({
        variant: "success",
        title: "Tax rate updated",
        description: `Platform tax set to ${(newTax * 100).toFixed(0)}%.`,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Save failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  return (
    <>
      <GridBand className="flex flex-wrap items-center justify-between gap-3 py-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-base font-semibold tracking-tight">
            <Percent className="h-4 w-4 text-primary" />
            Platform tax
          </h3>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Applied to every product's invoice: Interpretation, Realtime,
            Meeting Intel, and Personal Notes.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {dirty ? "1 pending change." : "No pending changes."}
          </span>
          <Button onClick={handleSave} disabled={!dirty || update.isPending}>
            {update.isPending ? (
              <>
                <LoaderCircle className="h-4 w-4 animate-spin" /> Saving…
              </>
            ) : (
              <>
                <Save className="h-4 w-4" /> Save tax
              </>
            )}
          </Button>
        </div>
      </GridBand>

      <GridCell>
        <div className="sm:max-w-xs">
          <FormFieldGroup
            label="Tax rate (%)"
            htmlFor="platform-tax-rate"
            hint="Platform-wide. Stored as decimal (20% → 0.2). Applied at invoice render time."
          >
            <Input
              id="platform-tax-rate"
              inputMode="decimal"
              placeholder="20"
              value={taxPercent}
              onChange={(e) => setTaxPercent(e.target.value)}
            />
          </FormFieldGroup>
        </div>
      </GridCell>
    </>
  );
}
